// faceDetector — wraps MediaPipe FaceLandmarker for FOUR sub-features:
//   * mediapipe.face_presence    (no-face detection)
//   * mediapipe.multiple_faces   (>=2 people in frame)
//   * mediapipe.head_pose        (looking down / sideways patterns)
//   * mediapipe.eye_gaze         (eyes pointed off-screen even when head is forward)
//
// One FaceLandmarker instance serves all four — much cheaper than running
// four separate detectors. The 478-point face mesh model includes iris
// landmarks (points 468-477) which we use for the eye_gaze sub-feature.
// Each sub-feature has its own state machine (see ../stateMachine.js) and
// its own feature flag (events from disabled sub-features are not emitted).
//
// Why one detector, three features:
//   * MediaPipe is the expensive part (model load + inference per frame).
//   * Face landmarks contain everything we need: face count, face position
//     for presence, transformation matrix for head pose.
//   * Server-side, each sub-feature still appears as its own event with
//     its own feature_key — so super-admin can toggle them independently.
//
// Public API:
//   createFaceDetector({ videoElement, emit, getFlag, getThreshold }) → controller
//   controller.start()  // begins detection loop
//   controller.stop()   // cleans up
//   controller.isReady()

import { loadVisionTasks, MODEL_URLS } from '../mediapipeLoader';
import { createDurationStateMachine } from '../stateMachine';
import { createGazePatternTracker } from './gazePatternTracker';

const FEATURE_PRESENCE = 'mediapipe.face_presence';
const FEATURE_MULTIPLE = 'mediapipe.multiple_faces';
const FEATURE_HEAD_POSE = 'mediapipe.head_pose';
const FEATURE_EYE_GAZE = 'mediapipe.eye_gaze';

// MediaPipe Face Landmarker model returns 478 landmarks per face. The
// iris-related indices below come from Google's documented topology
// (https://github.com/google/mediapipe/blob/master/mediapipe/modules/face_geometry/data/canonical_face_model_uv_visualization.png).
// We avoid using all 10 iris points and just take the centre of each iris
// + 2 corners per eye, which is enough for "looking forward vs off to one
// side" classification at our 5fps detection rate.
const LANDMARK_IRIS_LEFT_CENTER  = 468;  // candidate's left iris centre
const LANDMARK_IRIS_RIGHT_CENTER = 473;  // candidate's right iris centre
const LANDMARK_EYE_LEFT_INNER    = 133;  // inner corner of candidate's left eye
const LANDMARK_EYE_LEFT_OUTER    = 33;   // outer corner of candidate's left eye
const LANDMARK_EYE_RIGHT_INNER   = 362;  // inner corner of candidate's right eye
const LANDMARK_EYE_RIGHT_OUTER   = 263;  // outer corner of candidate's right eye

// Upper / lower eyelid midpoints. Needed for VERTICAL iris position —
// the primary signal for "looking at phone in bottom corner of laptop"
// which is the most common cheat pattern (candidates place phone next
// to the keyboard so it's just below the screen).
const LANDMARK_EYE_LEFT_UPPER    = 159;
const LANDMARK_EYE_LEFT_LOWER    = 145;
const LANDMARK_EYE_RIGHT_UPPER   = 386;
const LANDMARK_EYE_RIGHT_LOWER   = 374;

// Default detection cadence. The MediaPipe runtime can do 30fps on a modern
// laptop, but for proctoring we don't need that. 5fps is plenty for the
// behaviours we're looking at (multi-second sustained patterns) and cuts
// CPU usage by 6×.
const DEFAULT_FPS = 5;

/**
 * Read a numeric threshold from a feature-flag JSON blob with a fallback.
 * Keeps the detector resilient to the admin editing the JSON to nonsense.
 */
const num = (obj, key, fallback) => {
  const v = obj?.[key];
  if (v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Build a 3-feature face detector controller.
 *
 * @param {object} opts
 * @param {HTMLVideoElement} opts.videoElement  Live webcam video element.
 *   Already attached to the candidate's webcam stream; we only READ from it.
 * @param {(event: object) => void} opts.emit   Function from the event
 *   emitter — accepts {feature_key, severity, score_delta, message, evidence}.
 * @param {(key: string) => boolean} opts.getFlag  Returns whether a sub-feature
 *   is enabled (cheap, reads cached snapshot — no network).
 * @param {(key: string) => object} opts.getThreshold  Returns the feature's
 *   threshold JSON dict (defaults to {} for missing keys).
 * @param {number} [opts.fps]  Override the default detection rate (5 fps).
 */
export function createFaceDetector({
  videoElement,
  emit,
  getFlag,
  getThreshold,
  fps = DEFAULT_FPS,
}) {
  if (!videoElement) {
    throw new Error('[faceDetector] videoElement is required');
  }
  if (typeof emit !== 'function') {
    throw new Error('[faceDetector] emit must be a function');
  }
  if (typeof getFlag !== 'function' || typeof getThreshold !== 'function') {
    throw new Error('[faceDetector] getFlag and getThreshold must be functions');
  }

  let landmarker = null;
  let running = false;
  let intervalId = null;
  let ready = false;

  // ── State machines ────────────────────────────────────────────────────────
  // Built lazily inside start() because thresholds may have changed since
  // the controller was constructed.
  let smPresence = null;
  let smMultiple = null;
  let smHeadPose = null;
  // Eye gaze uses the richer GazePatternTracker (not a simple duration
  // state machine) because it watches for BOTH sustained gaze AND
  // repeated brief glances — the latter is the most common cheating
  // pattern with phones placed in corners of the laptop bezel.
  let gazeTracker = null;

  // ── Initial Bring-up ──────────────────────────────────────────────────────
  const ensureLandmarker = async () => {
    if (landmarker) return landmarker;
    const { vision, modules } = await loadVisionTasks();
    const { FaceLandmarker, FilesetResolver } = modules;
    void FilesetResolver; // silence unused-import warning in some bundlers

    landmarker = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URLS.faceLandmarker,
        delegate: 'GPU', // falls back to CPU automatically if WebGL is unavailable
      },
      runningMode: 'VIDEO',
      numFaces: 3, // cap — we only need to know "0", "1", or "2+"
      // Enable the transformation matrix output so we can read head pose.
      outputFacialTransformationMatrixes: true,
      outputFaceBlendshapes: false, // we don't use expressions yet
    });

    ready = true;
    return landmarker;
  };

  // ── Per-feature state machines (built when start() runs) ──────────────────
  const initStateMachines = () => {
    const thrPresence = getThreshold(FEATURE_PRESENCE);
    const thrMultiple = getThreshold(FEATURE_MULTIPLE);
    const thrHeadPose = getThreshold(FEATURE_HEAD_POSE);

    smPresence = createDurationStateMachine({
      warnAfterMs: num(thrPresence, 'warn_after_seconds', 3) * 1000,
      flagAfterMs: num(thrPresence, 'flag_after_seconds', 10) * 1000,
      onWarn: () => {
        emit({
          feature_key: FEATURE_PRESENCE,
          severity: 'low',
          score_delta: 1,
          message: 'No face detected — please face the camera',
          evidence: { stage: 'warn', warn_after_seconds: num(thrPresence, 'warn_after_seconds', 3) },
        });
      },
      onFlag: () => {
        emit({
          feature_key: FEATURE_PRESENCE,
          severity: 'high',
          score_delta: 8,
          message: 'No face in frame for sustained period',
          evidence: { stage: 'flag', flag_after_seconds: num(thrPresence, 'flag_after_seconds', 10) },
        });
      },
    });

    smMultiple = createDurationStateMachine({
      warnAfterMs: num(thrMultiple, 'min_duration_seconds', 1) * 1000,
      flagAfterMs: num(thrMultiple, 'min_duration_seconds', 1) * 1000, // same — no warn stage
      onWarn: () => {
        emit({
          feature_key: FEATURE_MULTIPLE,
          severity: 'critical',
          score_delta: 25,
          message: 'Multiple faces detected in frame',
          evidence: { stage: 'flag' },
        });
      },
      onFlag: () => {}, // already escalated at warn stage
    });

    smHeadPose = createDurationStateMachine({
      warnAfterMs: num(thrHeadPose, 'sustained_seconds', 2) * 1000,
      flagAfterMs: num(thrHeadPose, 'sustained_seconds', 2) * 1000 + 3000,
      onWarn: () => {
        emit({
          feature_key: FEATURE_HEAD_POSE,
          severity: 'medium',
          score_delta: 3,
          message: 'Head turned away from camera',
          evidence: { stage: 'warn' },
        });
      },
      onFlag: () => {
        emit({
          feature_key: FEATURE_HEAD_POSE,
          severity: 'high',
          score_delta: 6,
          message: 'Sustained head deviation — possible second-monitor or phone use',
          evidence: { stage: 'flag' },
        });
      },
    });

    const thrEyeGaze = getThreshold(FEATURE_EYE_GAZE);
    gazeTracker = createGazePatternTracker({
      // Pattern A: sustained off-screen episode
      warnAfterMs: num(thrEyeGaze, 'sustained_warn_seconds', 2) * 1000,
      flagAfterMs: num(thrEyeGaze, 'sustained_flag_seconds', 5) * 1000,
      // Pattern B: repeated brief glances in a sliding window
      windowMs: num(thrEyeGaze, 'repeat_window_seconds', 60) * 1000,
      minGlanceMs: num(thrEyeGaze, 'min_glance_ms', 300),
      maxGlanceMs: num(thrEyeGaze, 'max_glance_ms', 5000),
      glanceCountWarn: num(thrEyeGaze, 'glance_count_warn', 3),
      glanceCountFlag: num(thrEyeGaze, 'glance_count_flag', 5),

      onSustainedWarn: () => {
        emit({
          feature_key: FEATURE_EYE_GAZE,
          severity: 'medium',
          score_delta: 4,
          message: 'Eyes focused off-screen with head still — possible hidden phone',
          evidence: { stage: 'warn', pattern: 'sustained_off_screen' },
        });
      },
      onSustainedFlag: () => {
        emit({
          feature_key: FEATURE_EYE_GAZE,
          severity: 'high',
          score_delta: 8,
          message: 'Sustained off-screen gaze with neutral head pose — likely hidden device use',
          evidence: { stage: 'flag', pattern: 'sustained_off_screen' },
        });
      },

      // Pattern B handlers — the key new capability. These fire when the
      // candidate has done the "glance / type / glance / type" cycle
      // enough times in the sliding window. No legitimate scanning behaviour
      // produces this signature.
      onRepeatedWarn: ({ count, windowSeconds }) => {
        emit({
          feature_key: FEATURE_EYE_GAZE,
          severity: 'medium',
          score_delta: 5,
          message: `${count} off-screen glances in last ${windowSeconds}s — pattern consistent with hidden phone use`,
          evidence: {
            stage: 'warn',
            pattern: 'repeated_glances',
            count,
            window_seconds: windowSeconds,
          },
        });
      },
      onRepeatedFlag: ({ count, windowSeconds }) => {
        emit({
          feature_key: FEATURE_EYE_GAZE,
          severity: 'high',
          score_delta: 12,
          message: `${count} off-screen glances in last ${windowSeconds}s — strong indicator of hidden device use`,
          evidence: {
            stage: 'flag',
            pattern: 'repeated_glances',
            count,
            window_seconds: windowSeconds,
          },
        });
      },
    });
  };

  // ── Head-pose extraction from MediaPipe's transformation matrix ───────────
  // MediaPipe returns a 4x4 row-major transformation matrix. We extract
  // pitch/yaw/roll from the upper-left 3x3 rotation submatrix using the
  // standard Tait-Bryan extraction (ZYX intrinsic).
  // Returns degrees, never throws.
  const extractHeadPoseDegrees = (matrix4x4) => {
    if (!matrix4x4 || matrix4x4.length < 16) return null;
    // Row-major: row i col j = matrix4x4[i*4 + j]
    const m11 = matrix4x4[0];
    const m12 = matrix4x4[1];
    const m13 = matrix4x4[2];
    const m23 = matrix4x4[6];
    const m33 = matrix4x4[10];

    // Standard pitch-yaw-roll extraction (radians) for ZYX intrinsic rotation.
    const pitch = Math.atan2(-m23, m33);
    const yaw = Math.asin(Math.max(-1, Math.min(1, m13)));
    const roll = Math.atan2(-m12, m11);

    const rad2deg = 180 / Math.PI;
    return {
      pitch: pitch * rad2deg,
      yaw: yaw * rad2deg,
      roll: roll * rad2deg,
    };
  };

  // ── Eye-gaze estimate from iris + eye-corner + eyelid landmarks ───────────
  // Returns {x, y} ratios, each 0-1, averaged across both eyes.
  //   x: 0.0 = looking far left, 0.5 = centred, 1.0 = looking far right
  //   y: 0.0 = looking up (top of eye), 0.5 = centred, 1.0 = looking down
  // The Y axis is critical — the most common cheat is a phone placed in
  // the bottom corner of the laptop bezel, which requires the candidate
  // to glance DOWN-ish to read it. Without Y tracking, we'd miss that.
  const computeGazeRatio = (landmarks) => {
    if (!landmarks || landmarks.length < 478) return null;
    const need = [
      LANDMARK_IRIS_LEFT_CENTER, LANDMARK_IRIS_RIGHT_CENTER,
      LANDMARK_EYE_LEFT_INNER, LANDMARK_EYE_LEFT_OUTER,
      LANDMARK_EYE_RIGHT_INNER, LANDMARK_EYE_RIGHT_OUTER,
      LANDMARK_EYE_LEFT_UPPER, LANDMARK_EYE_LEFT_LOWER,
      LANDMARK_EYE_RIGHT_UPPER, LANDMARK_EYE_RIGHT_LOWER,
    ];
    for (const idx of need) {
      if (!landmarks[idx]) return null;
    }
    // Where does point P sit on the segment A→B? 0 = at A, 1 = at B.
    const ratio = (a, b, p, axis) => {
      const span = b[axis] - a[axis];
      if (Math.abs(span) < 1e-6) return 0.5;
      return (p[axis] - a[axis]) / span;
    };

    // ── Horizontal: iris position between outer and inner eye corners ─────
    const leftX = ratio(
      landmarks[LANDMARK_EYE_LEFT_OUTER],
      landmarks[LANDMARK_EYE_LEFT_INNER],
      landmarks[LANDMARK_IRIS_LEFT_CENTER],
      'x',
    );
    const rightX = ratio(
      landmarks[LANDMARK_EYE_RIGHT_INNER],
      landmarks[LANDMARK_EYE_RIGHT_OUTER],
      landmarks[LANDMARK_IRIS_RIGHT_CENTER],
      'x',
    );

    // ── Vertical: iris position between upper and lower eyelid ────────────
    // When eye is mostly closed (blink), the upper-lower span shrinks and
    // ratio gets noisy. We don't filter here because the state-machine
    // duration requirement (sustained N seconds) already absorbs blink-
    // related single-frame spikes.
    const leftY = ratio(
      landmarks[LANDMARK_EYE_LEFT_UPPER],
      landmarks[LANDMARK_EYE_LEFT_LOWER],
      landmarks[LANDMARK_IRIS_LEFT_CENTER],
      'y',
    );
    const rightY = ratio(
      landmarks[LANDMARK_EYE_RIGHT_UPPER],
      landmarks[LANDMARK_EYE_RIGHT_LOWER],
      landmarks[LANDMARK_IRIS_RIGHT_CENTER],
      'y',
    );

    return {
      x: (leftX + rightX) / 2,
      y: (leftY + rightY) / 2,
    };
  };

  // ── Detection tick — called at `fps` Hz ───────────────────────────────────
  const tick = () => {
    if (!running || !landmarker || !videoElement) return;
    if (videoElement.readyState < 2) return; // not enough data yet

    let result;
    try {
      result = landmarker.detectForVideo(videoElement, performance.now());
    } catch (err) {
      // A single tick failure shouldn't kill detection — log once per error
      // shape and move on. Don't spam the console at 5Hz.
      // eslint-disable-next-line no-console
      console.warn('[faceDetector] detectForVideo failed:', err?.message || err);
      return;
    }

    const faceCount = result?.faceLandmarks?.length || 0;
    const matrices = result?.facialTransformationMatrixes || [];

    // ── 1. Face presence ────────────────────────────────────────────────────
    if (getFlag(FEATURE_PRESENCE)) {
      // "Active" means the condition we want to flag IS happening = no face.
      smPresence.update(faceCount === 0);
    } else if (smPresence) {
      smPresence.reset();
    }

    // ── 2. Multiple faces ──────────────────────────────────────────────────
    if (getFlag(FEATURE_MULTIPLE)) {
      smMultiple.update(faceCount >= 2);
    } else if (smMultiple) {
      smMultiple.reset();
    }

    // ── 3. Head pose ───────────────────────────────────────────────────────
    if (getFlag(FEATURE_HEAD_POSE) && faceCount >= 1 && matrices[0]) {
      const thr = getThreshold(FEATURE_HEAD_POSE);
      const pitchDownLimit = num(thr, 'pitch_down_threshold_deg', 20);
      const yawSideLimit = num(thr, 'yaw_side_threshold_deg', 30);

      const pose = extractHeadPoseDegrees(matrices[0].data || matrices[0]);
      if (pose) {
        // "Active" = head is pointing meaningfully away from the screen.
        // Negative pitch = looking down (towards phone on lap).
        // Large |yaw| = looking left/right (towards second monitor).
        const pitchTriggered = pose.pitch < -pitchDownLimit;
        const yawTriggered = Math.abs(pose.yaw) > yawSideLimit;
        smHeadPose.update(pitchTriggered || yawTriggered);
      } else {
        smHeadPose.reset();
      }
    } else if (smHeadPose) {
      smHeadPose.reset();
    }

    // ── 4. Eye gaze ────────────────────────────────────────────────────────
    // Detects the classic "hidden phone" cheat: candidate places a phone in
    // the bottom-left or bottom-right corner of the laptop bezel (or just
    // beside the laptop) and reads it with their eyes only — keeping their
    // HEAD nearly still so they look like they're focused on the screen.
    //
    // Heuristic: flag ONLY when BOTH conditions hold for a sustained period:
    //   (a) Iris position is extreme on either axis (X or Y outside the
    //       configured "normal screen reading" range).
    //   (b) Head pose is near-neutral (yaw / pitch within head_neutral_*
    //       thresholds). This rules out legitimate "look at far edge of
    //       screen" behaviour — natural reading involves head follow.
    //
    // Why both: a candidate reading the problem statement on the far-left
    // panel naturally tilts their head left slightly. That trips (a) but
    // not (b), so it doesn't flag. Hidden-phone-cheating keeps the head
    // still — that trips BOTH.
    if (getFlag(FEATURE_EYE_GAZE) && faceCount >= 1 && result?.faceLandmarks?.[0]) {
      const thr = getThreshold(FEATURE_EYE_GAZE);
      // X bounds — anything outside is eyes pointing past the screen edge.
      const xMin = num(thr, 'gaze_x_min', 0.35);
      const xMax = num(thr, 'gaze_x_max', 0.65);
      // Y bounds — anything outside is eyes pointing above or below screen.
      // Asymmetric defaults: candidates can glance down at the keyboard
      // (y up to ~0.65 is normal); below 0.70 = phone-below-screen pattern.
      const yMin = num(thr, 'gaze_y_min', 0.30);
      const yMax = num(thr, 'gaze_y_max', 0.70);
      // Head-near-neutral bounds — if yaw or pitch exceed these, the head
      // is following the gaze, so we treat it as legitimate scanning.
      const headYawMax = num(thr, 'head_neutral_yaw_max_deg', 12);
      const headPitchMax = num(thr, 'head_neutral_pitch_max_deg', 10);

      const gaze = computeGazeRatio(result.faceLandmarks[0]);
      const pose = matrices[0]
        ? extractHeadPoseDegrees(matrices[0].data || matrices[0])
        : null;

      if (gaze && pose) {
        const eyeOff =
          gaze.x < xMin || gaze.x > xMax ||
          gaze.y < yMin || gaze.y > yMax;
        const headNeutral =
          Math.abs(pose.yaw) < headYawMax &&
          Math.abs(pose.pitch) < headPitchMax;
        // Feed the combined signal to the gaze pattern tracker. It handles
        // BOTH sustained-gaze AND repeated-glance bookkeeping internally
        // — each brief glance back to on-screen closes the current episode
        // and adds an entry to the sliding-window glance counter.
        gazeTracker.update(eyeOff && headNeutral);
      } else {
        // Missing landmarks or matrix; don't flag spuriously.
        gazeTracker.reset();
      }
    } else if (gazeTracker) {
      gazeTracker.reset();
    }
  };

  // ── Public methods ────────────────────────────────────────────────────────
  const start = async () => {
    if (running) return;
    await ensureLandmarker();
    initStateMachines();
    running = true;
    // setInterval is fine — at 5fps the drift vs requestAnimationFrame is
    // negligible and we don't have render-loop concerns.
    intervalId = setInterval(tick, Math.round(1000 / fps));
  };

  const stop = () => {
    running = false;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (smPresence) smPresence.reset();
    if (smMultiple) smMultiple.reset();
    if (smHeadPose) smHeadPose.reset();
    if (gazeTracker) gazeTracker.reset();
    if (landmarker) {
      try { landmarker.close(); } catch (_) { /* model already disposed */ }
      landmarker = null;
    }
    ready = false;
  };

  const isReady = () => ready;

  return { start, stop, isReady };
}
